import React from "react";
import { CatalogueProvider } from "./../Providers/CatalogueProvider";
import { Routes } from './../Core/Constants';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const CARET_LEFT = 158;
const MAX_WIDTH = "1020px";

function formatPrice(value) {
  return value.toLocaleString("cs-CZ") + " Kč";
}

function ProductItem({ product, position }) {
  const availabilityLabel = product.availability ? "Skladem" : "Není skladem";

  return (
    <a
      data-type="item"
      data-result-id={position}
      data-autocomplete-id={position}
      href={product.url}
      className={`luigi-ac-item luigi-ac-product luigi-ac-availability--${product.availability ? 1 : 0}`}
      data-autocomplete-value={product.title}
      itemProp="itemListElement"
      itemScope
      itemType="http://schema.org/Product http://schema.org/ListItem"
    >
      <meta itemProp="position" content={String(position)} />
      <meta itemProp="url" content={product.url} />
      <meta itemProp="type" content="item" />

      <div className="luigi-ac-image">
        <img src={product.imageLink} alt={product.title} itemProp="image" />
      </div>

      <div className="luigi-ac-description">
        <div className="luigi-ac-text">
          <div
            className="luigi-ac-name line-clamp line-clamp-2"
            title={product.title}
            itemProp="name"
            dangerouslySetInnerHTML={{
              __html: product.titleHighlighted || product.title
            }}
          />
          <div
            className="luigi-ac-attrs"
            title={availabilityLabel}
            itemProp="category"
            content={availabilityLabel}
          >
            <span className="luigi-ac-attr luigi-ac-attr--availability_rank_text">
              <span>{availabilityLabel}</span>
            </span>
          </div>
        </div>

        <div
          className={`luigi-ac-price${product.priceOld ? " luigi-ac-price-discount" : ""}`}
          itemProp="offers"
          itemScope
          itemType="http://schema.org/Offer"
        >
          <meta itemProp="priceCurrency" content="CZK" />
          <meta itemProp="price" content={String(product.price)} />
          {product.priceOld != null &&
            <div className="luigi-ac-price-old">{formatPrice(product.priceOld)}</div>
          }
          <span className="luigi-ac-price-label">Koncová cena</span>
          <div className="luigi-ac-price-new">{formatPrice(product.price)}</div>
        </div>
      </div>
    </a>
  );
}

export default class MeiliAutocomplete extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      result: null,
      currentQuery: "",
      isOpen: false,
      isLoading: false,
      error: null,
      activeTab: "item",
      positionStyle: {}
    };
    this.debounceTimer = null;
    this.latestQuery = "";
    this.wrapperRef = React.createRef();
    this.input = null;
  }

  componentDidMount() {
    if (!this.props.enabled) {
      return;
    }

    this.input = document.querySelector("#searchphrase");
    if (!this.input) {
      console.error("Není input #searchphrase");
      return;
    }
    this.input.addEventListener("input", this.handleInput);
    this.input.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("mousedown", this.handleClickOutside);
    window.addEventListener("resize", this.updatePosition);
  }

  componentWillUnmount() {
    if (this.input) {
      this.input.removeEventListener("input", this.handleInput);
      this.input.removeEventListener("keydown", this.handleKeyDown);
    }
    document.removeEventListener("mousedown", this.handleClickOutside);
    window.removeEventListener("resize", this.updatePosition);
    clearTimeout(this.debounceTimer);
  }

  updatePosition = () => {
    if (!this.input) { return; }
    const rect = this.input.getBoundingClientRect();
    const top = rect.bottom;
    const maxHeight = window.innerHeight - rect.bottom - 16;
    this.setState({
      positionStyle: {
        position: "fixed",
        top: top + "px",
        maxHeight: maxHeight + "px",
        maxWidth: MAX_WIDTH,
        left: "0px",
        right: "0px",
        margin: "0px auto"
      }
    });
  }

  handleInput = (event) => {
    const query = event.target.value.trim();
    clearTimeout(this.debounceTimer);

    if (query.length < MIN_QUERY_LENGTH) {
      this.setState({ isOpen: false, result: null, error: null });
      return;
    }

    this.setState({ currentQuery: query });
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(query);
    }, DEBOUNCE_MS);
  }

  handleKeyDown = (event) => {
    if (event.key === "Escape") {
      this.setState({ isOpen: false });
    }
  }

  handleClickOutside = (event) => {
    if (
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(event.target) &&
      this.input &&
      !this.input.contains(event.target)
    ) {
      this.setState({ isOpen: false });
    }
  }

  fetchResults = (query) => {
    this.latestQuery = query;
    this.updatePosition();
    this.setState({ isLoading: true, error: null });
    CatalogueProvider.getMeiliSuggestions(query, (data) => {
      if (query !== this.latestQuery) { return; }
      this.setState({ result: data, isOpen: true, isLoading: false });
    }, () => {
      if (query !== this.latestQuery) { return; }
      this.setState({ error: "Chyba při načítání výsledků.", isOpen: false, isLoading: false });
    });
  }

  handleClose = () => {
    this.setState({ isOpen: false });
  }

  render() {
    if (!this.props.enabled) { return null; }

    const { result, currentQuery, isOpen, isLoading, error, activeTab, positionStyle } = this.state;

    if (isLoading) {
      return (
        <div ref={this.wrapperRef}>
          <div className="luigi-ac-loading" aria-live="polite">Načítám…</div>
        </div>
      );
    }

    if (error) {
      return (
        <div ref={this.wrapperRef}>
          <div className="luigi-ac-error" role="alert">{error}</div>
        </div>
      );
    }

    if (!isOpen || !result) { return null; }

    const categories = result.categories || [];
    const products = result.products || [];
    const categoryCount = result.categoryCount != null ? result.categoryCount : categories.length;
    const productCount = result.productCount != null ? result.productCount : products.length;

    if (categories.length === 0 && products.length === 0) { return null; }

    const [firstProduct, ...restProducts] = products;

    const isEmpty = currentQuery.trim().length === 0;
    const categoryHeader = isEmpty ? "Populární kategorie" : "Kategorie";
    const productHeader = isEmpty ? "Populární produkty" : "Produkty";

    const rootClass = [
      "luigi-ac luigi-ac-heromobile luigi-ac-heromobile--right luigi-ac-autocomplete luigi-ac-open",
      isEmpty ? "luigi-ac-query-empty" : ""
    ].filter(Boolean).join(" ");

    const searchUrl = Routes.Search + "?q={search_term_string}";
    const showAllHref = Routes.Search + "?q=" + encodeURIComponent(currentQuery);
    const baseUrl = window.location.origin;

    return (
      <div ref={this.wrapperRef}>
        <div className={rootClass} id="lbx-ac-element" style={positionStyle}>
          <div itemScope itemType="http://schema.org/WebSite">
            <meta itemProp="url" content={baseUrl} />

            <div
              itemScope
              itemType="http://schema.org/SearchAction"
              itemProp="potentialAction"
            >
              <meta itemProp="target" content={`${baseUrl}${searchUrl}`} />
              <input
                style={{ display: "none" }}
                itemProp="query-input"
                type="text"
                name="search_term_string"
                required
                tabIndex={0}
                aria-label="ProduktyKategorie "
              />
              <meta itemProp="query" content={currentQuery} />

              <div style={{ display: "none" }} itemProp="instrument" itemScope itemType="http://schema.org/ChooseAction">
                <meta itemProp="name" content="_Variant" />
                <meta itemProp="actionOption" content="Luigis" />
              </div>
              <div style={{ display: "none" }} itemProp="instrument" itemScope itemType="http://schema.org/ChooseAction">
                <meta itemProp="name" content="Recommender" />
                <meta itemProp="actionOption" content="autocomplete_popup" />
              </div>
              <div style={{ display: "none" }} itemProp="instrument" itemScope itemType="http://schema.org/ChooseAction">
                <meta itemProp="name" content="RecommenderClientId" />
                <meta itemProp="actionOption" content="autocomplete_popup" />
              </div>

              <div
                className="luigi-ac-results-content"
                itemProp="result"
                itemScope
                itemType="http://schema.org/ItemList"
              >
                <meta itemProp="name" content={isEmpty ? "Recommendation" : "Autocomplete"} />

                <div className="luigi-ac-caret luigi-ac-hero-color" style={{ left: CARET_LEFT }} />
                <div className="luigi-ac-ribbon luigi-ac-hero-color" />

                <div className="luigi-ac-result" data-type="item">
                  <div className="luigi-ac-inner">
                    <div className="luigi-ac-heromobile-input-bg" />

                    <div className="luigi-ac-heromobile-switches">
                      <div className="luigi-ac-heromobile-switches-inner">
                        <div
                          className={[
                            "luigi-ac-heromobile-switch luigi-ac-heromobile-switch--first",
                            activeTab === "item" ? "luigi-ac-heromobile-switch--active" : ""
                          ].join(" ")}
                          data-type="item"
                          data-type-count={productCount}
                          onClick={() => this.setState({ activeTab: "item" })}
                        >
                          Produkty
                        </div>
                        <div
                          className={[
                            "luigi-ac-heromobile-switch",
                            activeTab === "category" ? "luigi-ac-heromobile-switch--active" : ""
                          ].join(" ")}
                          data-type="category"
                          data-type-count={categoryCount}
                          onClick={() => this.setState({ activeTab: "category" })}
                        >
                          Kategorie
                        </div>
                      </div>
                    </div>

                    <div className="luigi-ac-info" />

                    <div className="luigi-ac-others">
                      <div className="luigi-ac-header luigi-ac-header--category" data-type="category">
                        {categoryHeader}
                      </div>

                      {categories.map((cat, idx) => (
                        <a
                          key={cat.id || idx}
                          data-type="category"
                          data-result-id={idx + 1}
                          data-autocomplete-id={idx + 1}
                          href={cat.url}
                          className="luigi-ac-item luigi-ac-other"
                          data-autocomplete-value={cat.title}
                          itemProp="itemListElement"
                          itemScope
                          itemType="http://schema.org/CollectionPage http://schema.org/ListItem"
                        >
                          <meta itemProp="position" content={String(idx + 1)} />
                          <meta itemProp="url" content={cat.url} />
                          <meta itemProp="type" content="category" />
                          <div
                            className="luigi-ac-other-content"
                            title={cat.title}
                            itemProp="name"
                            dangerouslySetInnerHTML={{ __html: cat.titleHighlighted || cat.title }}
                          />
                        </a>
                      ))}
                    </div>

                    <div className="luigi-ac-products luigi-ac-main">
                      {firstProduct &&
                        <div className="luigi-ac-first-main">
                          <div
                            className="luigi-ac-header luigi-ac-header--item luigi-ac-heromobile-hidden-header"
                            data-type="item"
                          >
                            Top produkt
                          </div>
                          <div
                            className="luigi-ac-header luigi-ac-header--item luigi-ac-heromobile-fixed-header"
                            data-type="item"
                          >
                            {productHeader}
                          </div>
                          <ProductItem product={firstProduct} position={1} />
                        </div>
                      }

                      {restProducts.length > 0 &&
                        <div className="luigi-ac-rest-main luigi-ac-main--border">
                          <div className="luigi-ac-header luigi-ac-header--item" data-type="item">
                            {productHeader}
                          </div>
                          {restProducts.map((product, idx) => (
                            <ProductItem key={product.id || idx} product={product} position={idx + 2} />
                          ))}
                        </div>
                      }

                      {!isEmpty &&
                        <div
                          className="luigi-ac-button-block luigi-ac-button-block--show-all"
                          tabIndex={0}
                          role="button"
                          onClick={() => { window.location.href = showAllHref; }}
                        >
                          <div
                            className="luigi-ac-button luigi-ac-hero-color-clickable luigi-ac-button-heromobile luigi-ac-hero-color"
                            tabIndex={0}
                            role="button"
                          >
                            Zobrazit všechny produkty
                          </div>
                        </div>
                      }
                    </div>
                  </div>

                  <div className="luigi-ac-footer">
                    <i /> Powered by{" "}
                    <a target="_blank" rel="noreferrer" href="https://www.luigisbox.com/">
                      Luigi&apos;s Box
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="luigi-ac-close" onClick={this.handleClose} />
          </div>
        </div>
      </div>
    );
  }
}
